import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTable, useSortBy, usePagination, useExpanded, useRowSelect } from 'react-table'
import styled, { spacing } from '../theme/index.jsx'
import { SortDesc, SortAsc, SortIdle } from '@pergas-common/pergas-icons'

const StyledTable = styled.table`
  table-layout: fixed;
  width: 100%;
  min-width: 100%;
  height: auto;
  border-spacing: 0;
  border-collapse: collapse;

  box-shadow: 0 8px 5px -11px ${({ theme }) => theme.palette.common.secondary};
`

const TableScroller = styled.div`
  display: block;
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  max-width: 100%;
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;
`

const StyledCell = styled.td`
  box-sizing: border-box;
  padding: 0 ${spacing(1)};
  height: 48px;
  border: 1px solid #e4e9f0;
  &:first-child: {
    border-left: none;
  }
`

const StyledHead = styled.th`
  box-sizing: border-box;
  position: sticky;
  top: 0;
  z-index: 2;
  background: ${({ theme }) => theme.palette.table.primary};
  padding: 0 ${spacing(1)};
  height: 47px;
  border: 1px solid #e4e9f0;
  color: #FFFFFF;
  overflow: visible;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-transform: capitalize;
  line-height: 1.5rem;
  font-weight: ${({ strong }) => (strong ? 'bold !important' : 'normal')};
  box-shadow: ${({ isResizing }) => isResizing ? 'inset -2px 0 0 rgba(255, 255, 255, 0.85)' : 'none'};
  width: auto;
  text-align: left;
  svg {
    vertical-align: middle;
  }
  &:first-child: {
    border-left: none;
  },
`

const ResizeHandle = styled.div`
  position: absolute;
  top: 0;
  right: -7px;
  width: 14px;
  height: 100%;
  cursor: ew-resize;
  z-index: 3;
  background: ${({ isResizing }) => isResizing ? 'rgba(255, 255, 255, 0.28)' : 'rgba(255, 255, 255, 0.14)'};

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 2px;
    height: 100%;
    background: rgba(255, 255, 255, 0.92);
  }

  &:hover::after {
    background: #FFFFFF;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.32);
  }

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 4px;
    height: 16px;
    border-radius: 2px;
    background-image: radial-gradient(rgba(255, 255, 255, 0.98) 1.1px, transparent 1.1px);
    background-size: 3px 4px;
    background-position: center;
    opacity: ${({ isResizing }) => isResizing ? 1 : 0.92};
    pointer-events: none;
  }
`

const TableRow = styled.tr`
  height: 47px;
  &:hover: {
    cursor: ${({ onClick }) => onClick && 'pointer'};
    background: ${({ interactive = true }) => interactive && '#f7f9fb'};
  }
`

export const CellHolder = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${({ theme, isTh }) =>
    isTh ? theme.typography.subTitle : theme.typography.body};
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  ${({ isTh }) => !isTh && 'min-width: 0;'}
`

const Inner = styled.div`
  display: flex;
  min-width: 0;
  margin: auto;
  justify-content: space-between;
  align-items: center;
  text-overflow: ellipsis;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
`

const InnerChild = styled.div`
  display: flex;
  align-items: center;
`

const TableHeadRow = styled.thead`
  display: table-header-group;
`

const TableHeadSpan = styled.span`
  user-select: none;
`

const SortHolder = styled.div`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: ${spacing(0.5)};
`

const TableHead = ({
  children,
  onClick,
  icons,
  isSortable,
  isSorted,
  isSortedDesc,
  ...props
}) => {
  return (
    <StyledHead onClick={onClick} {...props} strong>
      <CellHolder isTh>
        {children}
        <SortHolder>
          {isSorted && (isSortedDesc ? <SortDesc style={{ color: '#64B5F6' }} /> : <SortAsc style={{ color: '#64B5F6' }} />)}
          {isSortable && !isSorted && <SortIdle />}
        </SortHolder>
      </CellHolder>
    </StyledHead>
  )
}

const TableCell = ({ className, children, left, center, right, ...rest }) => {
  return (
    <StyledCell {...rest}>
      <CellHolder>
        {children || (
          <Inner>
            {left && <InnerChild>{left}</InnerChild>}
            {center && <InnerChild>{center}</InnerChild>}
            {right && <InnerChild>{right}</InnerChild>}
          </Inner>
        )}
      </CellHolder>
    </StyledCell>
  )
}

const TableBody = styled.tbody`
  display: table-row-group;
  vertical-align: middle;
  border-color: inherit;
`

export const THead = (props) => <TableHeadRow {...props} />
export const Th = (props) => <TableHead {...props} />
export const Tr = (props) => <TableRow {...props} />
export const Td = (props) => <TableCell {...props} />
export const Tb = (props) => <TableBody {...props} />
export const Tf = (props) => <tfoot {...props} />

export const Table = ({
  data,
  children,
  manualSortBy = true,
  columns,
  onRowClick,
  renderRowSubComponent,
  initialPageSize = 10,
  selectedRowIds = {},
  initialSortBy = [],
  enableColumnResize = true,
  persistColumnResize = true,
  columnResizeStorageKey,
  defaultColumnMinWidth = 40,
  columnResizeStorageVersion = 'v2'
}) => {
  const [columnWidths, setColumnWidths] = useState({})
  const [resizingColumnId, setResizingColumnId] = useState(null)
  const resizeStateRef = useRef(null)
  const suppressNextHeaderClickRef = useRef(false)

  const resolvedColumnResizeStorageKey = useMemo(() => {
    if (!persistColumnResize) {
      return null
    }
    if (columnResizeStorageKey) {
      return `table-column-widths:${columnResizeStorageVersion}:${columnResizeStorageKey}`
    }
    if (typeof window === 'undefined') {
      return null
    }
    const columnKey = columns
      .map((column, index) => {
        if (column.id) {
          return column.id
        }
        if (typeof column.accessor === 'string') {
          return column.accessor
        }
        return `col-${index}`
      })
      .join('|')
    return `table-column-widths:${columnResizeStorageVersion}:${window.location.pathname}:${columnKey}`
  }, [columnResizeStorageVersion, columns, columnResizeStorageKey, persistColumnResize])

  const resolveColumnWidth = useCallback((column, nextWidth) => {
    if (typeof nextWidth === 'number') {
      return `${nextWidth}px`
    }
    if (typeof column.width === 'number') {
      return `${column.width}px`
    }
    if (typeof column.width === 'string') {
      return column.width
    }
    return null
  }, [])

  const stopColumnResize = useCallback(() => {
    if (!resizeStateRef.current) {
      return
    }

    document.body.style.cursor = ''
    document.body.style.userSelect = ''
    window.removeEventListener('mousemove', resizeStateRef.current.onMouseMove)
    window.removeEventListener('mouseup', resizeStateRef.current.onMouseUp)
    suppressNextHeaderClickRef.current = true
    window.setTimeout(() => {
      suppressNextHeaderClickRef.current = false
    }, 0)
    setResizingColumnId(null)
    resizeStateRef.current = null
  }, [])

  const onColumnResizeStart = useCallback((e, column, headerEl) => {
    e.preventDefault()
    e.stopPropagation()

    const startX = e.clientX
    const initialWidth = headerEl.getBoundingClientRect().width
    const minWidth = typeof column.minWidth === 'number' ? column.minWidth : defaultColumnMinWidth
    const maxWidth = typeof column.maxWidth === 'number' ? column.maxWidth : Number.POSITIVE_INFINITY

    const onMouseMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX
      const nextWidth = Math.max(minWidth, Math.min(maxWidth, Math.round(initialWidth + delta)))
      setColumnWidths((prev) => ({ ...prev, [column.id]: nextWidth }))
    }

    const onMouseUp = () => {
      stopColumnResize()
    }

    resizeStateRef.current = { onMouseMove, onMouseUp }
    setResizingColumnId(column.id)
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
  }, [defaultColumnMinWidth, stopColumnResize])

  useEffect(() => {
    return () => {
      stopColumnResize()
    }
  }, [stopColumnResize])

  useEffect(() => {
    if (!resolvedColumnResizeStorageKey || typeof window === 'undefined') {
      return
    }
    try {
      const persisted = window.localStorage.getItem(resolvedColumnResizeStorageKey)
      if (!persisted) {
        return
      }
      const parsed = JSON.parse(persisted)
      if (!parsed || typeof parsed !== 'object') {
        return
      }
      const sanitizedWidths = Object.entries(parsed).reduce((acc, [columnId, width]) => {
        if (typeof width === 'number' && Number.isFinite(width) && width >= 24) {
          acc[columnId] = Math.round(width)
        }
        return acc
      }, {})
      setColumnWidths(sanitizedWidths)
    } catch (err) {}
  }, [resolvedColumnResizeStorageKey])

  useEffect(() => {
    if (!resolvedColumnResizeStorageKey || typeof window === 'undefined') {
      return
    }
    try {
      window.localStorage.setItem(resolvedColumnResizeStorageKey, JSON.stringify(columnWidths))
    } catch (err) {}
  }, [columnWidths, resolvedColumnResizeStorageKey])

  const tableProps = useTable(
    {
      getResetExpandedDeps: false,
      paginateExpandedRows: false,
      autoResetHiddenColumns: false,
      disableMultiSort: true,
      manualSortBy: manualSortBy,
      columns,
      data,
      initialState: {
        pageSize: initialPageSize,
        sortBy: initialSortBy,
        selectedRowIds
      },
      autoResetSelectedRows: true
    },
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect
  )
  const {
    page,
    headerGroups,
    prepareRow,
    pageCount,
    gotoPage,
    setPageSize,
    toggleAllPageRowsSelected,
    state: { pageIndex, pageSize }
  } = tableProps

  const renderedChildren = children && children({ pageCount, pageSize, pageIndex, gotoPage, setPageSize })
  const orderedColumns = headerGroups[0]?.headers || []

  return (
    <TableScroller>
      <StyledTable {...tableProps.getTableProps()}>
        <>
          <colgroup>
            {orderedColumns.map((column) => {
              const resolvedWidth = resolveColumnWidth(column, columnWidths[column.id])
              const style = resolvedWidth
                ? { width: resolvedWidth }
                : undefined
              return <col key={column.id} style={style} />
            })}
          </colgroup>
          <THead>
            {headerGroups.map((headerGroup, i) => (
              <Tr key={i} interactive={false} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(
                  ({ manualSort, isSortable = true, ...column }) => {
                    return (
                      <Th
                        key={column.id}
                        size={column.size}
                        bold={column.bold}
                        isResizing={resizingColumnId === column.id}
                        isSorted={column.isSorted}
                        isSortedDesc={column.isSortedDesc}
                        isSortable={isSortable}
                        {...column.getHeaderProps(
                          isSortable && column.getSortByToggleProps()
                        )}
                        onClick={(e) => {
                          if (suppressNextHeaderClickRef.current) {
                            e.preventDefault()
                            e.stopPropagation()
                            return
                          }
                          if (isSortable) {
                            const { onClick } = column.getSortByToggleProps()
                            onClick && onClick(e)
                            manualSort && manualSort(column)
                          }
                        }}
                      >
                        <TableHeadSpan>{column.render('Header')}</TableHeadSpan>
                        {enableColumnResize && (
                          <ResizeHandle
                            title='Drag to resize column. Double-click to reset width.'
                            isResizing={resizingColumnId === column.id}
                            onClick={(e) => {
                              e.stopPropagation()
                            }}
                            onDoubleClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              setColumnWidths((prev) => {
                                if (!Object.prototype.hasOwnProperty.call(prev, column.id)) {
                                  return prev
                                }
                                const next = { ...prev }
                                delete next[column.id]
                                return next
                              })
                            }}
                            onMouseDown={(e) => {
                              const headerEl = e.currentTarget.parentElement
                              if (!headerEl) {
                                return
                              }
                              onColumnResizeStart(e, column, headerEl)
                            }}
                          />
                        )}
                      </Th>
                    )
                  }
                )}
              </Tr>
            ))}
          </THead>
          <Tb>
            {page.map((row) => {
              prepareRow(row)
              const styleProps = {}
              if (row.isSelected) {
                styleProps.style = { background: '#DAE1E5' }
              }
              return (
                <Fragment key={row.getRowProps().key}>
                  <Tr
                    {...styleProps}
                    onClick={
                      onRowClick
                        ? (event) => {
                            onRowClick(event, row, toggleAllPageRowsSelected)
                          }
                        : undefined
                    }
                  >
                    {row.cells.map((cell) => {
                      const cRenderer = cell.render('Cell', {
                        ...cell.getCellProps()
                      })
                      if (cell.column.accessor) {
                        return <Td {...cell.getCellProps()}>{cRenderer}</Td>
                      }
                      return cRenderer
                    })}
                  </Tr>
                  {row.isExpanded &&
                   renderRowSubComponent &&
                   renderRowSubComponent({ row, toggleAllPageRowsSelected })}
                </Fragment>
              )
            })}
          </Tb>
          {renderedChildren && (
            <Tf>
              <Tr>
                <>
                  {renderedChildren}
                </>
              </Tr>
            </Tf>
          )}
        </>
      </StyledTable>
    </TableScroller>
  )
}

export default Table
